'use client'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { useCallback, useState } from 'react'

const DONATION_BUTTON_CLASS_NAME =
  'relative z-20 h-full min-h-[44px] w-full min-w-0 border-[#484BD0] bg-white! px-3 font-semibold text-[#484BD0]! hover:bg-[#EEF2FF]! hover:text-[#484BD0]!'

function DonationCampaignButton({ campaign, className }) {
  return (
    <Button
      asChild
      variant="outline"
      className={cn(DONATION_BUTTON_CLASS_NAME, className)}
    >
      <a href={campaign.donationUrl} target="_blank" rel="noopener noreferrer">
        Donate
      </a>
    </Button>
  )
}

function DonationCampaignDialog({ campaign, open, onOpenChange }) {
  if (campaign.presentation !== 'dialog') return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-3rem)] max-w-lg border-neutral-blue-700 bg-neutral-blue-900 p-6 text-shades-0 dark:border-neutral-blue-700 dark:bg-neutral-blue-900">
        <DialogHeader className="pr-8 text-left">
          <DialogTitle className="text-h-5 font-semibold leading-tight text-shades-0">
            {campaign.title}
          </DialogTitle>
          <DialogDescription className="pt-2 text-p-1 leading-6 text-neutral-blue-200 dark:text-neutral-blue-200">
            {campaign.description}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="mt-2 sm:justify-start">
          <Button
            asChild
            className="bg-linear-to-b hover:from-primary-600! hover:to-primary-600! border-none from-primary-500 to-[#FF3D00] text-white"
          >
            <a
              href={campaign.donationUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => onOpenChange(false)}
            >
              Donate now
            </a>
          </Button>
          <DialogClose asChild>
            <Button variant="outline" type="button">
              Maybe later
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default function DonationCampaignCta({
  campaign,
  children = null,
  className,
  open,
  onOpenChange,
}) {
  const [internalOpen, setInternalOpen] = useState(false)
  const dialogOpen = open ?? internalOpen
  const handleOpenChange = useCallback(
    (nextOpen) => {
      if (onOpenChange) {
        onOpenChange(nextOpen)
        return
      }

      setInternalOpen(nextOpen)
    },
    [onOpenChange],
  )

  if (!campaign) return children

  return (
    <>
      <div
        className={cn(
          'relative z-20 flex h-11 w-full min-w-0 gap-3',
          className,
        )}
      >
        {children ? (
          <div className="flex h-full min-w-0 flex-1">{children}</div>
        ) : null}
        <DonationCampaignButton
          campaign={campaign}
          className={children ? 'flex-1' : null}
        />
      </div>

      <DonationCampaignDialog
        campaign={campaign}
        open={dialogOpen}
        onOpenChange={handleOpenChange}
      />
    </>
  )
}
